<template>
  <div class="reg-cd">
    <div class="countdown-wrapper">
      <div class="countdown-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
        </svg>
      </div>

      <h2 class="countdown-title">Inscrições em breve</h2>
      <p class="countdown-subtitle">
        As inscrições para este evento serão abertas em breve.<br>
        Fique atento ao horário de abertura.
      </p>

      <div class="countdown-timer">
        <div class="countdown-block" :class="{ 'is-active': active.d, tick: bounce.d }">
          <div class="countdown-value">{{ display.d }}</div>
          <div class="countdown-label">Dias</div>
        </div>
        <div class="countdown-sep"><span></span><span></span></div>
        <div class="countdown-block" :class="{ 'is-active': active.h, tick: bounce.h }">
          <div class="countdown-value">{{ display.h }}</div>
          <div class="countdown-label">Horas</div>
        </div>
        <div class="countdown-sep"><span></span><span></span></div>
        <div class="countdown-block" :class="{ 'is-active': active.m, tick: bounce.m }">
          <div class="countdown-value">{{ display.m }}</div>
          <div class="countdown-label">Min</div>
        </div>
        <div class="countdown-sep"><span></span><span></span></div>
        <div class="countdown-block" :class="{ 'is-active': active.s, tick: bounce.s }">
          <div class="countdown-value">{{ display.s }}</div>
          <div class="countdown-label">Seg</div>
        </div>
      </div>

      <div v-if="formattedTarget" class="countdown-date">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
          <path d="M2 7h12M5 1v3M11 1v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        Abertura em <strong>{{ formattedTarget }}</strong>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';

const UNITS = ['d', 'h', 'm', 's'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

export default {
  name: 'RegistrationCountdown',
  props: {
    opensAt: {
      type: [String, Number, Date],
      default: null
    }
  },
  emits: ['opened'],
  setup(props, { emit }) {
    const display = ref({ d: '00', h: '00', m: '00', s: '00' });
    const active = ref({ d: false, h: false, m: false, s: false });
    const bounce = ref({ d: false, h: false, m: false, s: false });
    let timer = null;

    // Converte a data de abertura ("YYYY-MM-DD HH:mm:ss" do MySQL) para timestamp local
    const target = computed(() => {
      if (!props.opensAt) return null;
      if (props.opensAt instanceof Date) return props.opensAt.getTime();
      const t = new Date(String(props.opensAt).replace(' ', 'T')).getTime();
      return isNaN(t) ? null : t;
    });

    const formattedTarget = computed(() => {
      if (!target.value) return '';
      const d = new Date(target.value);
      const day = d.getDate();
      const month = MONTHS[d.getMonth()];
      const year = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${day} de ${month}, ${year} às ${hh}:${mm}`;
    });

    const pad = (n) => String(n).padStart(2, '0');

    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };

    const refresh = () => {
      const t = target.value;
      if (!t) return;
      const diff = t - Date.now();

      if (diff <= 0) {
        display.value = { d: '00', h: '00', m: '00', s: '00' };
        stop();
        emit('opened');
        return;
      }

      const next = {
        d: pad(Math.floor(diff / 86400000)),
        h: pad(Math.floor((diff % 86400000) / 3600000)),
        m: pad(Math.floor((diff % 3600000) / 60000)),
        s: pad(Math.floor((diff % 60000) / 1000))
      };

      UNITS.forEach((u) => {
        if (next[u] !== display.value[u]) {
          display.value[u] = next[u];
          active.value[u] = true;
          // Reinicia a animação de "tick": remove e re-adiciona a classe
          bounce.value[u] = false;
          nextTick(() => { bounce.value[u] = true; });
        }
      });
    };

    onMounted(() => {
      if (target.value && Date.now() >= target.value) {
        emit('opened');
        return;
      }
      refresh();
      timer = setInterval(refresh, 1000);
    });

    onBeforeUnmount(stop);

    return { display, active, bounce, formattedTarget };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

.reg-cd {
  --cd-accent: var(--button-color, #4f6ef7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.countdown-wrapper {
  width: 100%;
  max-width: 520px;
  text-align: center;
}

/* Icon */
.countdown-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--cd-accent) 10%, #ffffff),
    color-mix(in srgb, var(--cd-accent) 22%, #ffffff));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cd-accent);
  position: relative;
}

.countdown-icon::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 20px;
  border: 2px solid color-mix(in srgb, var(--cd-accent) 12%, transparent);
  animation: cdIconPulse 3s ease-in-out infinite;
}

@keyframes cdIconPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0; transform: scale(1.15); }
}

.countdown-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a1d26;
  letter-spacing: -0.02em;
  margin-bottom: 6px;
}

.countdown-subtitle {
  font-size: 0.8125rem;
  color: #7b8294;
  margin-bottom: 32px;
  line-height: 1.5;
}

/* Timer blocks */
.countdown-timer {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 32px;
}

.countdown-block {
  flex: 1;
  max-width: 100px;
  background: #fff;
  border: 1px solid #e2e5ee;
  border-radius: 14px;
  padding: 20px 8px 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(26, 29, 38, 0.04);
}

.countdown-block::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg,
    var(--cd-accent),
    color-mix(in srgb, var(--cd-accent) 55%, #ffffff));
  opacity: 0;
  transition: opacity 0.3s;
}

.countdown-block.is-active::before {
  opacity: 1;
}

.countdown-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 2rem;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1;
  margin-bottom: 6px;
}

.countdown-block.tick .countdown-value {
  animation: cdTickBounce 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes cdTickBounce {
  0% { transform: scale(1); }
  30% { transform: scale(1.12); }
  100% { transform: scale(1); }
}

.countdown-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3b4;
}

/* Separator */
.countdown-sep {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding-bottom: 18px;
}

.countdown-sep span {
  width: 5px;
  height: 5px;
  background: #d1d5db;
  border-radius: 50%;
  display: block;
}

/* Date badge — border-radius padrão da LP (não pílula) */
.countdown-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #fff;
  border: 1px solid #e2e5ee;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5068;
  box-shadow: 0 2px 8px rgba(26, 29, 38, 0.04);
}

.countdown-date svg {
  color: var(--cd-accent);
  flex-shrink: 0;
}

.countdown-date strong {
  color: #1a1d26;
  font-weight: 700;
}

@media (max-width: 420px) {
  .countdown-timer { gap: 8px; }
  .countdown-value { font-size: 1.5rem; }
  .countdown-block { padding: 16px 6px 12px; border-radius: 12px; }
  .countdown-sep span { width: 4px; height: 4px; }
}
</style>
